import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Send, RefreshCw, Loader2, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { API_URL, useAuth } from '@/contexts/AuthContext';
import WorkflowStatusFilter, { WorkflowChip, getWorkflowConfig } from '@/components/WorkflowStatusChips';
import PagamentoPagarme from '@/components/PagamentoPagarme';
import PagamentoBelluno from '@/components/PagamentoBelluno';
import ComprovantesPedido from '@/components/ComprovantesPedido';
import { PageCover, PageBody, CoverButton, SheetHeader, StatusDot, DOT } from '@/components/Page';
import { linksDoPedido } from '@/lib/belluno';
import { formatBRL, formatBRLCompacto, formatNumero, formatData, formatDataHora, formatDuracao, formatRelativo } from '@/lib/format';
import {
  FINANCIAMENTO, aguardandoFinanceira, condicaoFinanciamento, diasDesdeAprovacao, emMinusculas, rotuloFormas, tomDoPedido,
} from '@/lib/pagamento';

// Fila dos pedidos de financiamento que a financeira ainda não pagou (não é um status: é a forma + o pagamento).
const A_RECEBER = 'financiamento_a_receber';

// Etapas do filtro ("Filtrar" + chip). O padrão é a fila que exige ação do financeiro.
const etapa = (label, tone) => ({ label, tone, color: DOT[tone] });
const FILTROS = {
  comprovante_em_analise: etapa('Comprovante em análise', 'att'),
  revisao_antifraude: etapa('Revisão antifraude', 'att'),
  envio_manual: etapa('Envio manual', 'att'),
  pagamento_confirmado: etapa('Pagamento confirmado', 'run'),
  erro_tiny: etapa('Erro no Tiny', 'bad'),
  aguardando_pagamento: etapa('Aguardando pagamento', 'run'),
  [A_RECEBER]: etapa('Financiamentos a receber', 'run'),
  rejeitado: etapa('Rejeitados', 'bad'),
  enviado_tiny: etapa('Enviados ao Tiny', 'ok'),
};

// "aprovado em 13/10/2026, há 3 dias": há quanto tempo a financeira deve o pedido.
const desdeAprovacao = (o) => {
  const dias = diasDesdeAprovacao(o);
  if (dias === null) return '';
  return `aprovado em ${formatData(o.financiamento.aprovado_em)}, ${dias === 0 ? 'hoje' : `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`}`;
};

// Hoje no calendário de quem registra (AAAA-MM-DD), o padrão da data do recebimento.
const hojeLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Etapas em que o pedido espera uma decisão do financeiro (números da capa).
const FILA = 'comprovante_em_analise,revisao_antifraude,pagamento_confirmado,erro_tiny';

// Só pedido com cobrança na Pagar.me (cartão, PIX pelo checkout ou parte em cartão do split) consulta a Pagar.me.
const usaPagarme = (o) => o.forma_pagamento === 'cartao_credito'
  || (o.pagarme_order_ids || []).length > 0
  || (o.pagamento_split || []).some((p) => p.metodo === 'credit_card');

// Desde quando o pedido está na etapa atual: último evento que o levou até ela.
const desdeEtapa = (o) => {
  const ev = [...(o.historico || [])].reverse().find((h) => h.para === o.workflow_status);
  const d = ev?.em || o.updated_at || o.created_at;
  return d ? new Date(d) : null;
};

const Secao = ({ titulo, children }) => (
  <section className="mt-6 border-t border-border pt-5">
    <h3 className="mb-3 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-gray-500">{titulo}</h3>
    {children}
  </section>
);

const Financeiro = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('comprovante_em_analise');
  const [actionId, setActionId] = useState(null); // pedido em processamento
  const [selecionadoId, setSelecionadoId] = useState(null);
  const [rejeitando, setRejeitando] = useState(false);
  const [rejectMotivo, setRejectMotivo] = useState('');
  // Registrar recebimento (financiamento): data, observação e o comprovante da financeira, opcional.
  const [recebendo, setRecebendo] = useState(false);
  const [recebidoEm, setRecebidoEm] = useState('');
  const [obsRecebimento, setObsRecebimento] = useState('');
  const [arquivoRecebimento, setArquivoRecebimento] = useState(null);
  const [fila, setFila] = useState(null);
  const docRef = useRef(null);

  const isAuthorized = user?.role === 'administrativo' || user?.role === 'master';

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // ponytail: na fila a receber o servidor filtra a forma e a tela tira os pagos e cancelados. Teto: os 500
      // pedidos de financiamento mais recentes; filtrar "não pago" no servidor se um dia passar disso.
      const params = filtro === A_RECEBER ? { forma: FINANCIAMENTO, limit: 500 } : { limit: 200 };
      // "Envio manual" não é um status: é a marca do pedido (fila de liberação manual ao Tiny).
      if (filtro === 'envio_manual') params.envio_manual = true;
      else if (filtro !== 'all' && filtro !== A_RECEBER) params.workflow_status = filtro;
      const response = await axios.get(`${API_URL}/pedidos`, { params });
      const lista = response.data || [];
      // A receber: da aprovação mais antiga para a mais nova (quem espera há mais tempo vem primeiro).
      setOrders(filtro === A_RECEBER
        ? lista.filter(aguardandoFinanceira)
          .sort((a, b) => String(a.financiamento?.aprovado_em || '').localeCompare(String(b.financiamento?.aprovado_em || '')))
        : lista);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  // Capa: tudo o que espera uma decisão do financeiro, independente do filtro aberto.
  const fetchFila = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/pedidos`, { params: { workflow_status: FILA, limit: 200 } });
      const lista = data || [];
      const datas = lista.map(desdeEtapa).filter(Boolean);
      setFila({
        n: lista.length,
        valor: lista.reduce((s, o) => s + (Number(o.total) || 0), 0),
        maisAntigo: datas.length ? new Date(Math.min(...datas)) : null,
      });
    } catch {
      setFila(null);
    }
  }, []);

  useEffect(() => {
    if (isAuthorized) fetchOrders();
  }, [fetchOrders, isAuthorized]);

  useEffect(() => {
    if (isAuthorized) fetchFila();
  }, [fetchFila, isAuthorized]);

  const atualizar = async () => {
    await Promise.all([fetchOrders(), fetchFila()]);
  };

  const selecionado = orders.find((o) => o.id === selecionadoId) || orders[0] || null;
  const comps = selecionado?.comprovantes || [];

  // Troca de pedido: fecha a rejeição e o recebimento em andamento (o visualizador volta sozinho ao primeiro comprovante).
  useEffect(() => {
    setRejeitando(false);
    setRejectMotivo('');
    setRecebendo(false);
  }, [selecionado?.id]);

  const abrirRecebimento = () => {
    setRecebidoEm(hojeLocal());
    setObsRecebimento('');
    setArquivoRecebimento(null);
    setRecebendo(true);
  };

  // Marca pago sem reenviar ao Tiny (o pedido de financiamento foi na aprovação). Multipart por causa do comprovante.
  const handleRegistrarRecebimento = async (order) => {
    setActionId(order.id);
    try {
      const form = new FormData();
      form.append('recebido_em', recebidoEm);
      if (obsRecebimento.trim()) form.append('obs', obsRecebimento.trim());
      if (arquivoRecebimento) form.append('comprovante', arquivoRecebimento);
      await axios.post(`${API_URL}/pedidos/${order.id}/financiamento/recebimento`, form);
      toast.success(`Recebimento do ${order.numero_pedido} registrado`);
      setRecebendo(false);
      await atualizar();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao registrar o recebimento');
      // 409: outra pessoa registrou, ou o pedido foi estornado ou cancelado; a ficha estava velha.
      if (error.response?.status === 409) {
        setRecebendo(false);
        await atualizar();
      }
    } finally {
      setActionId(null);
    }
  };

  const selecionar = (id) => {
    setSelecionadoId(id);
    if (window.innerWidth < 1280) docRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleAprovar = async (order) => {
    setActionId(order.id);
    try {
      // Aprovar pagamento JÁ dispara o envio ao Tiny (split por filial).
      const response = await axios.post(`${API_URL}/pedidos/${order.id}/aprovar-pagamento`, { obs: null });
      const data = response.data || {};
      if (data.tiny_dispatch_error) {
        toast.warning(`Pagamento do ${order.numero_pedido} confirmado, mas o envio ao Tiny falhou. Use "Reenviar ao Tiny".`);
      } else if (data.tiny_dispatch && data.tiny_dispatch.all_ok === false) {
        toast.warning(data.message || 'Pagamento confirmado; envio ao Tiny com erros, reprocessar.');
      } else {
        toast.success(`Pagamento do ${order.numero_pedido} confirmado e enviado ao Tiny`);
      }
      await atualizar();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao aprovar pagamento');
    } finally {
      setActionId(null);
    }
  };

  const handleConfirmReject = async (order) => {
    if (!rejectMotivo.trim()) {
      toast.error('Informe o motivo da rejeição');
      return;
    }
    setActionId(order.id);
    try {
      await axios.post(`${API_URL}/pedidos/${order.id}/rejeitar-pagamento`, { motivo: rejectMotivo.trim() });
      toast.success(`Comprovante do ${order.numero_pedido} rejeitado`);
      setRejeitando(false);
      setRejectMotivo('');
      await atualizar();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao rejeitar pagamento');
    } finally {
      setActionId(null);
    }
  };

  const handleEnviarTiny = async (order) => {
    setActionId(order.id);
    try {
      const response = await axios.post(`${API_URL}/pedidos/${order.id}/enviar-tiny`, {});
      if (response.data?.all_ok === false) {
        toast.warning(response.data.message || 'Despacho concluído com erros em uma ou mais filiais');
      } else {
        toast.success(`${order.numero_pedido} enviado ao Tiny`);
      }
      await atualizar();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao enviar ao Tiny');
    } finally {
      setActionId(null);
    }
  };

  if (!isAuthorized) {
    return (
      <>
        <PageCover title="Financeiro" subtitle="Área exclusiva do time financeiro" />
        <PageBody>
          <Card className="p-7">
            <StatusDot tone="bad">Acesso restrito</StatusDot>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Peça acesso a um administrador se precisar desta tela.</p>
          </Card>
        </PageBody>
      </>
    );
  }

  const figuras = fila
    ? [
        { value: formatBRLCompacto(fila.valor), label: 'esperando o financeiro' },
        { value: `${formatNumero(fila.n)}${fila.n >= 200 ? '+' : ''}`, label: fila.n === 1 ? 'pedido na fila' : 'pedidos na fila' },
        ...(fila.maisAntigo ? [{ value: formatDuracao(fila.maisAntigo), label: 'o mais antigo esperando' }] : []),
      ]
    : [];

  // Função de renderização (não componente): o documento não remonta a cada tecla do motivo.
  const renderDocumento = (order) => {
    const wf = order.workflow_status || 'aguardando_pagamento';
    const cfg = getWorkflowConfig(wf);
    const busy = actionId === order.id;
    const fin = order.forma_pagamento === FINANCIAMENTO ? order.financiamento || {} : null;
    // Financiamento não tem comprovante a aprovar: a financeira paga e o recebimento é registrado aqui.
    const podeAprovar = !fin && (wf === 'comprovante_em_analise' || wf === 'aguardando_pagamento');
    const podeReceber = aguardandoFinanceira(order);
    const podeEnviarTiny = wf === 'pagamento_confirmado' || wf === 'erro_tiny';
    const emRevisao = wf === 'revisao_antifraude';
    const entrega = order.endereco_entrega || {};
    const cobranca = order.endereco_cobranca || {};
    // num pedido em duas partes valem as formas das partes ("cartão + PIX"), não a escolhida na criação
    const forma = rotuloFormas(order);
    const parcelas = !(order.pagamento_split || []).length && order.forma_pagamento === 'cartao_credito' && order.parcelamento > 1
      ? ` em ${order.parcelamento}x` : '';

    return (
      <article className="p-6 sm:p-7">
        <p className="text-[12.5px] text-gray-500">
          {[order.numero_pedido, order.created_at && `criado ${formatDataHora(order.created_at)}`, order.created_by_name].filter(Boolean).join(' · ')}
        </p>
        <h2 className="mt-1.5 text-[22px] font-medium leading-tight tracking-[-0.01em]">{order.cliente_nome}</h2>
        <p className="mt-3.5 text-[36px] font-normal leading-none tracking-[-0.03em] tabular-nums">{formatBRL(order.total)}</p>
        <div className="mt-3">
          <StatusDot tone={tomDoPedido(order, cfg.tone)}>
            {cfg.label}{forma ? `, ${emMinusculas(forma)}${parcelas}` : ''} {formatRelativo(desdeEtapa(order))}
          </StatusDot>
        </div>

        {!rejeitando && !recebendo && (podeAprovar || podeReceber || podeEnviarTiny || emRevisao) && (
          <div className="mt-5 flex flex-wrap gap-2">
            {podeReceber && (
              <Button size="sm" disabled={busy} onClick={abrirRecebimento} data-testid={`registrar-recebimento-${order.numero_pedido}`}>
                <Banknote className="h-4 w-4" />
                Registrar recebimento
              </Button>
            )}
            {podeAprovar && (
              <>
                <Button size="sm" disabled={busy} onClick={() => handleAprovar(order)} data-testid={`aprovar-${order.numero_pedido}`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Aprovar e enviar ao Tiny
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-700 hover:text-red-800 dark:text-red-300"
                  disabled={busy}
                  onClick={() => setRejeitando(true)}
                  data-testid={`rejeitar-${order.numero_pedido}`}
                >
                  <XCircle className="h-4 w-4" />
                  Rejeitar
                </Button>
              </>
            )}
            {podeEnviarTiny && (
              <Button size="sm" disabled={busy} onClick={() => handleEnviarTiny(order)} data-testid={`enviar-tiny-${order.numero_pedido}`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {wf === 'erro_tiny' ? 'Reenviar ao Tiny' : 'Enviar ao Tiny'}
              </Button>
            )}
            {emRevisao && (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  // ponytail: confirm nativo — liberar um pedido retido por suspeita de
                  // fraude é sensível, mas não justifica um dialog.
                  if (window.confirm(
                    `${order.antifraude_divergencia === false ? 'Retido pelo antifraude' : 'Entrega difere da cobrança'} (${order.antifraude_motivo || 'CEPs divergentes'}).\n\n`
                    + 'Confirmar que revisou e deseja LIBERAR o despacho ao Tiny?'
                  )) handleEnviarTiny(order);
                }}
                data-testid={`liberar-antifraude-${order.numero_pedido}`}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Liberar e enviar
              </Button>
            )}
          </div>
        )}

        {rejeitando && (
          <div className="mt-5 space-y-2 rounded-[10px] bg-sheet2 p-4">
            <Label htmlFor="motivo-rejeicao">Motivo da rejeição</Label>
            <Textarea
              id="motivo-rejeicao"
              value={rejectMotivo}
              onChange={(e) => setRejectMotivo(e.target.value)}
              placeholder="Ex: comprovante ilegível, valor divergente, pagamento não identificado"
              rows={3}
              autoFocus
              data-testid="motivo-rejeicao-input"
            />
            <p className="text-[12.5px] text-gray-500">O pedido volta para o vendedor com este motivo.</p>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="destructive" disabled={busy} onClick={() => handleConfirmReject(order)} data-testid="confirmar-rejeicao">
                {busy ? 'Rejeitando' : 'Rejeitar comprovante'}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => { setRejeitando(false); setRejectMotivo(''); }}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {recebendo && (
          <div className="mt-5 space-y-3 rounded-[10px] bg-sheet2 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="recebido-em">Data do recebimento</Label>
                <Input
                  id="recebido-em" type="date" value={recebidoEm} max={hojeLocal()} disabled={busy} autoFocus
                  onChange={(e) => setRecebidoEm(e.target.value)} data-testid="recebido-em-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="comprovante-recebimento">Comprovante (opcional)</Label>
                <Input
                  id="comprovante-recebimento" type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                  disabled={busy} onChange={(e) => setArquivoRecebimento(e.target.files?.[0] || null)}
                  data-testid="comprovante-recebimento-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="obs-recebimento">Observação</Label>
              <Textarea
                id="obs-recebimento" rows={2} value={obsRecebimento} disabled={busy}
                onChange={(e) => setObsRecebimento(e.target.value)} placeholder="Ex: TED da financeira, extrato do dia"
                data-testid="obs-recebimento-input"
              />
            </div>
            <p className="text-[12.5px] text-gray-500">Marca o pedido como pago. Nada é enviado de novo ao Tiny.</p>
            <div className="flex gap-2 pt-1">
              <Button size="sm" disabled={busy || !recebidoEm} onClick={() => handleRegistrarRecebimento(order)} data-testid="confirmar-recebimento">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                {busy ? 'Registrando' : 'Registrar recebimento'}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setRecebendo(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {order.envio_manual && (
          <p className="mt-3 text-[13px] text-amber-800 dark:text-amber-300">
            {['Envio manual', order.envio_manual_motivo, order.envio_manual_por && `por ${order.envio_manual_por}`].filter(Boolean).join(' · ')}
          </p>
        )}
        {wf === 'rejeitado' && order.verificacao?.motivo_rejeicao && (
          <p className="mt-3 text-[13px] text-red-700 dark:text-red-300">Motivo da rejeição: {order.verificacao.motivo_rejeicao}</p>
        )}

        {emRevisao && (
          <Secao titulo="Antifraude">
            <p className="text-sm font-medium">{order.antifraude_motivo || 'Entrega difere da cobrança'}</p>
            <dl className="mt-2 space-y-1 text-[13.5px]">
              <div className="flex gap-3">
                <dt className="w-20 shrink-0 text-gray-500">Entrega</dt>
                <dd>
                  {[entrega.endereco || entrega.logradouro, entrega.numero || 's/n'].filter(Boolean).join(', ')}, {[entrega.cidade, entrega.estado].filter(Boolean).join('/')}, CEP {entrega.cep || 'não informado'}
                </dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-20 shrink-0 text-gray-500">Cobrança</dt>
                <dd>
                  {cobranca.linha || 'não informada'}, {[cobranca.cidade, cobranca.uf].filter(Boolean).join('/')}, CEP {cobranca.cep || 'não informado'}
                </dd>
              </div>
            </dl>
          </Secao>
        )}

        {fin && (
          <Secao titulo="Financiamento">
            {podeReceber && <StatusDot tone="run">Aguardando a financeira</StatusDot>}
            <dl className="mt-2 space-y-1 text-[13.5px]">
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 text-gray-500">Condição</dt>
                <dd>{condicaoFinanciamento(fin) || 'Não informada'}</dd>
              </div>
              {fin.aprovado_em && (
                <div className="flex gap-3">
                  <dt className="w-24 shrink-0 text-gray-500">Aprovação</dt>
                  <dd>{[desdeAprovacao(order), fin.aprovado_por_nome && `por ${fin.aprovado_por_nome}`].filter(Boolean).join(', ')}</dd>
                </div>
              )}
              {fin.recebido_em && (
                <div className="flex gap-3">
                  <dt className="w-24 shrink-0 text-gray-500">Recebimento</dt>
                  <dd>{[formatData(fin.recebido_em), fin.recebido_por_nome && `registrado por ${fin.recebido_por_nome}`].filter(Boolean).join(', ')}</dd>
                </div>
              )}
            </dl>
          </Secao>
        )}

        {comps.length > 0 && (
          <Secao titulo={comps.length === 1 ? 'Comprovante' : `Comprovantes (${comps.length})`}>
            <ComprovantesPedido key={order.id} pedido={order} />
          </Secao>
        )}

        {linksDoPedido(order).map(({ parte, titulo }) => (
          <Secao key={parte ?? 'pedido'} titulo={titulo}>
            <PagamentoBelluno pedido={order} parte={parte} onChanged={() => fetchOrders()} />
          </Secao>
        ))}
        {usaPagarme(order) && (
          <Secao titulo="Dados do pagamento (Pagar.me)">
            <PagamentoPagarme pedido={order} />
          </Secao>
        )}
      </article>
    );
  };

  return (
    <div data-testid="financeiro-page">
      <PageCover
        title="Financeiro"
        subtitle="Confira comprovantes, aprove pagamentos e despache pedidos ao Tiny"
        actions={
          <CoverButton onClick={atualizar} disabled={loading}>
            <RefreshCw className={loading ? 'animate-spin' : ''} />
            Atualizar
          </CoverButton>
        }
        figures={figuras}
      />
      <PageBody>
        {/* grid-cols-1 (minmax(0, 1fr)) no celular: sem ele a coluna cresce até a linha mais longa do documento */}
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(300px,3fr)_minmax(0,7fr)]">
          <Card className="overflow-hidden">
            <SheetHeader title={loading ? 'Carregando' : `${formatNumero(orders.length)} ${orders.length === 1 ? 'pedido' : 'pedidos'}`}>
              <div data-testid="financeiro-filtro">
                <WorkflowStatusFilter value={filtro} onChange={setFiltro} config={FILTROS} />
              </div>
            </SheetHeader>
            {loading ? (
              <ul aria-hidden>
                {Array.from({ length: 5 }).map((_, i) => (
                  <li key={i} className="space-y-2 border-b border-border px-5 py-4">
                    <span className="flex justify-between gap-4"><span className="h-4 w-40 animate-pulse rounded bg-muted" /><span className="h-4 w-20 animate-pulse rounded bg-muted" /></span>
                    <span className="block h-3 w-56 animate-pulse rounded bg-muted" />
                  </li>
                ))}
              </ul>
            ) : orders.length === 0 ? (
              <p className="px-5 py-12 text-sm text-gray-500">{filtro === A_RECEBER ? 'Nenhum financiamento a receber.' : 'Nenhum pedido nesta etapa.'}</p>
            ) : (
              <ul className="max-h-[calc(100vh-12rem)] overflow-y-auto">
                {orders.map((order) => {
                  const ativo = selecionado?.id === order.id;
                  const n = (order.comprovantes || []).length;
                  // Na fila a receber: a financeira e há quanto tempo ela aprovou.
                  const aReceber = filtro === A_RECEBER;
                  return (
                    <li key={order.id}>
                      <button
                        type="button"
                        onClick={() => selecionar(order.id)}
                        aria-current={ativo ? 'true' : undefined}
                        data-testid={`financeiro-row-${order.numero_pedido}`}
                        className={`grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 border-b border-border px-5 py-3.5 text-left transition-colors duration-150 ${
                          ativo ? 'bg-blue-50 dark:bg-blue-500/10' : 'hover:bg-sheet2'
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium">{order.cliente_nome}</span>
                          <span className="block truncate text-[12.5px] text-gray-500">
                            {[order.numero_pedido, order.created_by_name].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <span className="text-right">
                          <span className="block text-[15px] font-medium tabular-nums">{formatBRL(order.total)}</span>
                          <span className="block text-[12px] text-gray-500">
                            {aReceber ? order.financiamento?.financeira || rotuloFormas(order)
                              : n > 0 ? `${n} ${n === 1 ? 'comprovante' : 'comprovantes'}` : rotuloFormas(order)}
                          </span>
                        </span>
                        <span className="col-span-2 flex min-w-0 items-center gap-2">
                          <WorkflowChip wf={order.workflow_status || 'aguardando_pagamento'} pedido={order} className="shrink-0" />
                          <span className="truncate text-[12.5px] text-gray-500">
                            {aReceber ? desdeAprovacao(order)
                              : [order.envio_manual && 'envio manual', formatRelativo(desdeEtapa(order))].filter(Boolean).join(', ')}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card ref={docRef} className="xl:sticky xl:top-5 xl:max-h-[calc(100vh-2.5rem)] xl:overflow-y-auto">
            {selecionado ? (
              renderDocumento(selecionado)
            ) : (
              <p className="p-7 text-sm text-gray-500">{loading ? 'Carregando.' : 'A fila está vazia nesta etapa.'}</p>
            )}
          </Card>
        </div>
      </PageBody>
    </div>
  );
};

export default Financeiro;
